<template>
	<div class="active-accounts">
		<h2 v-if="isPreviewEnv && hasAccountsForEnv">
			Accounts for this environment:
		</h2>
		<h2 v-if="isPreviewEnv && !hasAccountsForEnv">
			No active accounts for this environment
		</h2>
		<div class="account-container">
			<div
				class="account-row"
				v-for="acct in accountsForEnv"
				:key="acct.id"
				:data-testid="acct.id"
			>
				<slot :account="acct">
					<BasicUserPill
						:profile="acct.organization"
						:mainText="acct.organization.name"
						:subText="acct.firstname + ' ' + acct.lastname"
						:link="acct.origin"
					>
						<div
							v-if="acct.isPreview"
							class="account-origin"
							v-text="acct.hostname"
						/>
					</BasicUserPill>
					<slot name="launcher" :account="acct">
						<Button
							pronto
							:href="acct.origin"
							:loading="isLoadingAccount === acct.id"
							@click="isLoadingAccount = acct.id"
							:aria-label="
								$t('onboarding_login_launch_alt_text', {
									orgName: acct.organization.name,
									userName: acct.firstname + ' ' + acct.lastname,
								})
							"
						>
							{{ $t('onboarding_accountpicker_launch') }}
						</Button>
					</slot>
				</slot>
				<div class="account-remove" @click="removeAccount(acct)">
					<CloseIcon />
				</div>
			</div>
		</div>
		<div
			v-if="isPreviewEnv"
			class="another-account"
			@click="anotherAccount()"
			tabindex="0"
			@keyup.enter="anotherAccount()"
		>
			{{ $t('chat_drawer_accountpicker_addaccount') }}
		</div>
		<slot name="header">
			<h2
				v-text="
					isPreviewEnv
						? 'Accounts for other environments:'
						: $t('onboarding_accountpicker_accounts_signedinto')
				"
			/>
		</slot>
		<div class="account-container">
			<div
				class="account-row"
				v-for="acct in otherAccounts"
				:key="acct.id"
				:data-testid="acct.id"
			>
				<slot :account="acct">
					<BasicUserPill
						:profile="acct.organization"
						:mainText="acct.organization.name"
						:subText="acct.firstname + ' ' + acct.lastname"
						:link="acct.origin"
					>
						<div
							v-if="acct.isPreview"
							class="account-origin"
							v-text="acct.hostname"
						/>
					</BasicUserPill>
					<slot name="launcher" :account="acct">
						<Button
							pronto
							:href="acct.origin"
							:loading="isLoadingAccount === acct.id"
							@click="isLoadingAccount = acct.id"
							:aria-label="
								$t('onboarding_login_launch_alt_text', {
									orgName: acct.organization.name,
									userName: acct.firstname + ' ' + acct.lastname,
								})
							"
						>
							{{ $t('onboarding_accountpicker_launch') }}
						</Button>
					</slot>
				</slot>
				<div class="account-remove" @click="removeAccount(acct)">
					<CloseIcon />
				</div>
			</div>
		</div>
		<div
			class="another-account"
			@click="anotherAccount()"
			tabindex="0"
			@keyup.enter="anotherAccount()"
		>
			{{ $t('chat_drawer_accountpicker_addaccount') }}
		</div>

		<SimpleModal v-if="overAccountLimit" @close="overAccountLimit = false">
			<div class="account-limit-warning">
				<div class="new-team-wrapper">
					<div class="upload-title font22">
						{{ $t('onboarding_account_maximum', [maxAccounts]) }}
					</div>
				</div>
				<div class="center m-b-25">
					<button
						class="btnActive"
						@click="overAccountLimit = false"
						aria-label="OK"
					>
						{{ $t('common_button_ok') }}
					</button>
				</div>
			</div>
		</SimpleModal>
	</div>
</template>

<script>
import {
	setDesktopLoginUrls,
	deleteCookie,
	getCookieDomain,
} from '@/helperMethods'
import { BasicUserPill, Button, SimpleModal } from '../common'
import { CloseIcon } from '@/icons'

const { origin } = window.location
const envOriginUri = origin.split('--').at(1)

export default {
	name: 'ActiveAccounts',
	props: ['accounts'],
	components: { BasicUserPill, Button, SimpleModal, CloseIcon },
	data() {
		return {
			overAccountLimit: false,
			maxAccounts: 30,
			isLoadingAccount: false,
		}
	},
	emits: ['another-account', 'refresh'],
	mounted() {
		setDesktopLoginUrls(this.accounts)
	},
	computed: {
		isPreviewEnv() {
			return window.location.origin.includes('--')
		},
		hasAccountsForEnv() {
			return this.accountsForEnv?.length > 0
		},
		accountsForEnv() {
			if (!envOriginUri) return []
			return this.accounts.filter((a) => a.origin.endsWith(envOriginUri))
		},
		otherAccounts() {
			if (!this.hasAccountsForEnv) return this.accounts
			return this.accounts.filter((a) => !a.origin.endsWith(envOriginUri))
		},
	},
	methods: {
		anotherAccount() {
			if (this.accounts.length >= this.maxAccounts) {
				this.overAccountLimit = true
			} else {
				this.$emit('another-account')
			}
		},
		removeAccount(account) {
			if (!account?.cookie_id) {
				return false
			}
			deleteCookie(account.cookie_id, getCookieDomain())
			console.debug('Login', 'removed active account cookie', account.cookie_id)
			this.$emit('refresh')
		},
	},
	watch: {
		accounts: {
			handler() {
				setDesktopLoginUrls(this.accounts)
			},
			deep: true,
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.active-accounts {
	@include flex-column-center;
	.another-account {
		cursor: pointer;
		width: 100%;
		padding: 16px;
		text-align: center;
		border: 1px dashed #c7cace;
		color: #c7cace;
		display: block;
		margin-top: 16px;
		border-radius: 5px;
		&:hover {
			background: #f8f8f8;
		}
	}
	.account-origin {
		font-size: 12;
	}
}
.account-container {
	width: 100%;
	flex-grow: 1;
	.account-row {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: space-between;
		cursor: pointer;
		padding: 16px 0;
		border-bottom: 1px solid var(--neutral-200);
		&:last-child {
			border-bottom: 0;
		}
		&:hover {
			.account-remove {
				opacity: 1;
			}
		}
		.account-origin {
			font-size: 12px;
			font-family: Avenir-Book;
			color: var(--neutral-500);
		}
		.account-remove {
			cursor: pointer;
			position: absolute;
			right: -28px;
			opacity: 0;
			&:hover {
				.pronto-icon {
					color: var(--neutral-800);
				}
			}
			.pronto-icon {
				font-size: 20px;
				color: var(--neutral-300);
			}
		}
	}
}

.account-limit-warning {
	padding-left: 50px;
	padding-right: 50px;
	width: 630px;
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.active-accounts {
		.another-account {
			&:hover {
				background: var(--neutral-200);
			}
		}
	}
}
</style>
